import { useMemo, useState } from "react"

import { ContentObjectItem, ContentObjectType } from "@vertesia/common"
import { FileBox, Loader } from "lucide-react"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@ui/select"

interface GenerateMetadataButtonProps {
    document: ContentObjectItem
    onActionDone?: () => void
    onGenerationDone: () => void
}
export function GenerateMetadataButton({ document, onActionDone, onGenerationDone }: Readonly<GenerateMetadataButtonProps>) {
    const { store } = useUserSession()

    const [open, setOpen] = useState(false)
    const [generationType, setGenerationType] = useState<string | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)
    const [filterValue, setFilterValue] = useState("")

    const { typeRegistry } = useUserSession()
    const types = useMemo(() => {
        return typeRegistry?.types || ([] as ContentObjectType[])
    }, [typeRegistry?.types])

    const filteredOptions = types.filter((type) => type.name.toLowerCase().includes(filterValue.toLowerCase()))

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleOpen = () => {
        setOpen(true)
        setIsLoading(false)
        setGenerationType(undefined)
    }

    const handleClose = () => {
        setOpen(false)
        setIsLoading(false)
        setGenerationType(undefined)
        onActionDone?.()
    }

    const onGenerate = () => {
        if (!generationType) {
            return
        }

        setIsLoading(true)
        store
            .runOperation({
                name: "change_type",
                ids: [document.id],
                params: { typeId: generationType },
            })
            .then(() => {
                handleClose()
                onGenerationDone()
            })
    }

    return (
        <>
            <Button onClick={handleOpen} variant="ghost" className="text-left justify-start w-full">
                <FileBox />
                <span>Regenerate Metadata</span>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Regenerate Metadata</DialogTitle>
                    </DialogHeader>
                    {isLoading && (
                        <div className="flex flex-col w-full items-center justify-center gap-2">
                            <span>Processing Metadata</span>
                            <Loader className="h-10 w-10 animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                        </div>
                    )}
                    {!isLoading && (
                        <>
                            <span> Do you want to regenerate the metadata for this document ?</span>
                            <div>
                                <Label>Document Type</Label>
                                <Select onValueChange={setGenerationType}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {types && types.length > 5 && (
                                            <div className="p-2">
                                                <Input placeholder="Search Types..." value={filterValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} />
                                            </div>
                                        )}
                                        <SelectGroup className="max-h-[200px] overflow-y-auto">
                                            {filteredOptions.map((type) => (
                                                <SelectItem key={type.id} value={type.id}>
                                                    {type.name}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        <Button variant="destructive" onClick={handleClose} disabled={isLoading} className="hover:cursor-pointer">
                            Cancel
                        </Button>
                        <Button variant="outline" onClick={onGenerate} disabled={isLoading || !generationType} className="hover:cursor-pointer">
                            {isLoading ? "Generating..." : "Regenerate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
